import React, { useState, useEffect } from "react";
import { Calendar, Plus, CheckCircle, Trash2, FileText } from "lucide-react";
import toast from "react-hot-toast";

const NIGERIAN_REGULATORS = [
  "NUPRC", "NESREA", "FMENV", "CAC", "FIRS", "PENCOM", "NSITF", "ITF", "NAFDAC", "SON", "NCC", "CBN", "SEC", "NIMASA", "NCAA", "BPP"
];

const COMPLIANCE_TEMPLATES = [
  { title: "Annual Returns Filing (CAC)", category: "Legal", regulator: "CAC", recurrence: "Annual", description: "File annual returns with Corporate Affairs Commission", daysBeforeAlert: 30 },
  { title: "Company Income Tax Filing", category: "Tax", regulator: "FIRS", recurrence: "Annual", description: "File CIT returns with Federal Inland Revenue Service", daysBeforeAlert: 45 },
  { title: "VAT Monthly Returns", category: "Tax", regulator: "FIRS", recurrence: "Monthly", description: "File monthly VAT returns", daysBeforeAlert: 7 },
  { title: "PENCOM Quarterly Returns", category: "Regulatory", regulator: "PENCOM", recurrence: "Quarterly", description: "Pension compliance quarterly filing", daysBeforeAlert: 14 },
  { title: "NSITF Annual Renewal", category: "Regulatory", regulator: "NSITF", recurrence: "Annual", description: "Renew NSITF compliance certificate", daysBeforeAlert: 30 },
  { title: "ITF Training Levy", category: "Regulatory", regulator: "ITF", recurrence: "Annual", description: "Pay Industrial Training Fund levy (1% of payroll)", daysBeforeAlert: 30 },
  { title: "ISO Certification Renewal", category: "Quality", regulator: "ISO", recurrence: "Annual", description: "Renew ISO 9001/14001/45001 certification", daysBeforeAlert: 60 },
  { title: "NUPRC Operating License Renewal", category: "Oil & Gas", regulator: "NUPRC", recurrence: "Annual", description: "Renew oil and gas operating license", daysBeforeAlert: 90 },
];

const PRIORITY_COLORS = {
  high: { color: "var(--accent-red)", dim: "var(--accent-red-dim)" },
  medium: { color: "var(--accent-orange)", dim: "var(--accent-orange-dim)" },
  low: { color: "var(--accent-blue)", dim: "var(--accent-blue-dim)" },
};

export default function RegulatoryCalendar() {
  const [tasks, setTasks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [form, setForm] = useState({ title: "", category: "", regulator: "", dueDate: "", priority: "medium", description: "", recurrence: "Once" });

  useEffect(() => {
    const saved = localStorage.getItem("protrackng_compliance_tasks");
    if (saved) setTasks(JSON.parse(saved));
  }, []);

  const save = (updated) => { setTasks(updated); localStorage.setItem("protrackng_compliance_tasks", JSON.stringify(updated)); };

  const addTask = () => {
    if (!form.title || !form.dueDate) { toast.error("Title and due date required"); return; }
    const newTask = { ...form, id: Date.now(), completed: false, createdAt: new Date().toISOString() };
    save([...tasks, newTask]);
    setForm({ title: "", category: "", regulator: "", dueDate: "", priority: "medium", description: "", recurrence: "Once" });
    setShowAdd(false);
    toast.success("Compliance task added!");
  };

  const addTemplate = (template) => {
    const dueDate = new Date();
    dueDate.setMonth(dueDate.getMonth() + 1);
    const newTask = { ...template, id: Date.now(), completed: false, priority: "medium", dueDate: dueDate.toISOString().split("T")[0], createdAt: new Date().toISOString() };
    save([...tasks, newTask]);
    toast.success(`"${template.title}" added to calendar!`);
  };

  const toggleComplete = (id) => { save(tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t)); };
  const deleteTask = (id) => { save(tasks.filter(t => t.id !== id)); toast.success("Task removed"); };

  const today = new Date();
  const upcoming = tasks.filter(t => !t.completed && new Date(t.dueDate) >= today).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const overdue = tasks.filter(t => !t.completed && new Date(t.dueDate) < today).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const completed = tasks.filter(t => t.completed);

  const getDaysUntil = (dateStr) => {
    const diff = Math.ceil((new Date(dateStr) - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const TaskCard = ({ task }) => {
    const days = getDaysUntil(task.dueDate);
    const isOverdue = days < 0;
    const { color } = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
    const urgentColor = isOverdue ? "var(--accent-red)" : days <= 7 ? "var(--accent-orange)" : days <= 30 ? "var(--accent-blue)" : color;

    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "16px", background: task.completed ? "var(--bg-secondary)" : "var(--bg-card)", border: `1px solid ${task.completed ? "var(--border)" : urgentColor + "40"}`, borderLeft: `3px solid ${task.completed ? "var(--text-muted)" : urgentColor}`, borderRadius: "12px", opacity: task.completed ? 0.6 : 1, transition: "all 0.15s" }}>
        <button onClick={() => toggleComplete(task.id)} style={{ background: "none", border: "none", cursor: "pointer", marginTop: "2px", flexShrink: 0 }}>
          {task.completed ? <CheckCircle size={20} color="var(--accent-green)" /> : <div style={{ width: "20px", height: "20px", borderRadius: "50%", border: `2px solid ${urgentColor}` }} />}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "600", textDecoration: task.completed ? "line-through" : "none", marginBottom: "3px" }}>{task.title}</div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {task.regulator && <span style={{ fontSize: "11px", background: "var(--accent-green-dim)", color: "var(--accent-green)", padding: "2px 8px", borderRadius: "100px" }}>{task.regulator}</span>}
                {task.category && <span style={{ fontSize: "11px", background: "rgba(255,255,255,0.05)", color: "var(--text-muted)", padding: "2px 8px", borderRadius: "100px" }}>{task.category}</span>}
                {task.recurrence && task.recurrence !== "Once" && <span style={{ fontSize: "11px", background: "rgba(79,142,247,0.1)", color: "var(--accent-blue)", padding: "2px 8px", borderRadius: "100px" }}>{task.recurrence}</span>}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: "12px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: task.completed ? "var(--text-muted)" : urgentColor }}>
                {task.completed ? "Done" : isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? "Today!" : `${days}d left`}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{new Date(task.dueDate).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</div>
            </div>
          </div>
          {task.description && <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.5" }}>{task.description}</p>}
        </div>
        <button onClick={() => deleteTask(task.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = "var(--accent-red)"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}>
          <Trash2 size={15} />
        </button>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <div style={{ width: "40px", height: "40px", background: "rgba(79,142,247,0.1)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Calendar size={20} color="var(--accent-blue)" />
            </div>
            <h1 style={{ fontSize: "24px" }}>Regulatory Calendar</h1>
          </div>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>{overdue.length > 0 ? `?? ${overdue.length} overdue tasks` : `${upcoming.length} upcoming compliance tasks`}</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn btn-ghost" onClick={() => setShowTemplates(!showTemplates)}><FileText size={15} /> Templates</button>
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}><Plus size={15} /> Add Task</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "24px" }}>
        {[
          { label: "Total Tasks", value: tasks.length, color: "var(--accent-blue)" },
          { label: "Upcoming", value: upcoming.length, color: "var(--accent-green)" },
          { label: "Overdue", value: overdue.length, color: "var(--accent-red)" },
          { label: "Completed", value: completed.length, color: "var(--text-muted)" },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ textAlign: "center", padding: "16px" }}>
            <div style={{ fontSize: "28px", fontFamily: "Syne", fontWeight: "800", color }}>{value}</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Templates Panel */}
      {showTemplates && (
        <div className="card" style={{ marginBottom: "20px", borderColor: "var(--accent-blue)" }}>
          <h3 style={{ fontSize: "14px", marginBottom: "14px", color: "var(--accent-blue)" }}>Nigerian Compliance Templates � Click to add</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {COMPLIANCE_TEMPLATES.map(t => (
              <button key={t.title} onClick={() => addTemplate(t)} style={{ padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", background: "var(--bg-secondary)", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.background = "rgba(79,142,247,0.05)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg-secondary)"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>{t.title}</span>
                  <span style={{ fontSize: "11px", background: "var(--accent-green-dim)", color: "var(--accent-green)", padding: "1px 6px", borderRadius: "100px" }}>{t.regulator}</span>
                </div>
                <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{t.recurrence} � {t.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add Task Form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: "20px", borderColor: "var(--accent-green)" }}>
          <h3 style={{ fontSize: "14px", marginBottom: "16px" }}>Add Compliance Task</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. CAC Annual Returns Filing" />
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Regulator</label>
              <select value={form.regulator} onChange={e => setForm(f => ({ ...f, regulator: e.target.value }))}>
                <option value="">Select Regulator</option>
                {NIGERIAN_REGULATORS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Due Date *</label>
              <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description of this compliance task..." />
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-primary" onClick={addTask}><Plus size={14} /> Add Task</button>
            <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", background: "var(--bg-secondary)", padding: "4px", borderRadius: "10px", width: "fit-content" }}>
        {[
          { id: "upcoming", label: `Upcoming (${upcoming.length})` },
          { id: "overdue", label: `Overdue (${overdue.length})`, alert: overdue.length > 0 },
          { id: "completed", label: `Completed (${completed.length})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: "7px 14px", borderRadius: "8px", border: "none", background: activeTab === tab.id ? "var(--bg-card)" : "transparent", color: activeTab === tab.id ? (tab.alert ? "var(--accent-red)" : "var(--accent-green)") : "var(--text-secondary)", cursor: "pointer", fontSize: "13px", fontWeight: "500", transition: "all 0.15s" }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {activeTab === "upcoming" && (upcoming.length === 0 ? <div className="card" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>No upcoming tasks. Add tasks or use templates above.</div> : upcoming.map(t => <TaskCard key={t.id} task={t} />))}
        {activeTab === "overdue" && (overdue.length === 0 ? <div className="card" style={{ textAlign: "center", padding: "40px", color: "var(--accent-green)" }}>? No overdue tasks!</div> : overdue.map(t => <TaskCard key={t.id} task={t} />))}
        {activeTab === "completed" && (completed.length === 0 ? <div className="card" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>No completed tasks yet.</div> : completed.map(t => <TaskCard key={t.id} task={t} />))}
      </div>
    </div>
  );
}
